import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import {
  Button,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Booking, BookingService, BookingStatus } from 'us-web-services';
import PageStyles from '../../styles/PageStyles';
import { BookingConstants } from '../../constants';

function BookingActions() {
  const { classes } = PageStyles();
  const [ids, setIds] = useState([]);
  const [idText, setIdText] = useState('');
  const [processing, setProcessing] = useState(false);
  const [batchType, setBatchType] = useState('Status');
  const [statusType, setStatusType] = useState('');

  const BATCH_LIMIT = 20;
  const BATCH_TYPES = [
    {
      id: 1,
      name: 'Status',
    },
  ];

  const statusColors = {
    Ready: '#d9edf7',
    Failure: '#f2dede',
    'Invalid ID': '#fcf8e3',
    Success: '#dff0d8',
  };

  const setSuccess = id => {
    id.status = 'Success';
  };

  const setFailure = id => {
    id.status = 'Failure';
  };

  const updateStatusType = event => {
    setStatusType(event.target.value);
  };
  const updateBatchType = event => {
    setBatchType(event.target.value);
  };

  const updateIdText = event => {
    setIdText(event.target.value);
  };

  const getStatus = id => {
    if (!Number.isInteger(+id) || +id <= 0) {
      return 'Invalid ID';
    }

    return 'Ready';
  };

  const parseIds = () => {
    const newIds = [];

    idText.split('\n').forEach(id => {
      newIds.push({
        id,
        status: newIds.length > BATCH_LIMIT ? 'Exceeded Limit' : getStatus(id),
      });
    });
    setIds(newIds);
  };

  const clearIds = () => {
    setIds([]);
  };

  const updateStatus = async id => {
    const data: Partial<Booking> = {
      status: statusType as BookingStatus,
    };

    try {
      await BookingService.patch(
        id.id,
        data,
      );
      setSuccess(id);
    } catch {
      setFailure(id);
    }
  };

  const updateStatuses = () => {
    const idsClone = [...ids];
    const promiseArray = [];

    setProcessing(true);

    idsClone.forEach(id => {
      if (id.status === 'Ready') {
        promiseArray.push(updateStatus(id));
      }
    });

    Promise.all(promiseArray).then(() => {
      setIds(idsClone);
      setProcessing(false);
    });
  };

  return (
    <Paper className={classes.paper}>
      <Typography component='h5' variant='h5' style={{ marginBottom: 15 }}>
        Batch Booking Actions
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <TextField
            required
            id='batchType'
            name='batchType'
            label='Batch Type'
            select
            fullWidth
            value={batchType}
            onChange={updateBatchType}
          >
            {BATCH_TYPES.map(type => (
              <MenuItem key={type.id} value={type.name}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
      <br />
      {ids.length === 0 ? (
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant='h6' gutterBottom>
              Enter booking ids on separate lines:
            </Typography>
            <TextField
              id='add-ids-field'
              variant='outlined'
              multiline
              minRows='8'
              value={idText}
              onChange={updateIdText}
            />
            <br /> <br />
            <Button
              id='parse-ids-button'
              variant='contained'
              color='primary'
              onClick={parseIds}
            >
              Parse IDs
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Id</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ids.map(id => (
                  <TableRow
                    key={id.id}
                    style={{ backgroundColor: statusColors[id.status] }}
                  >
                    <TableCell>{id.id}</TableCell>
                    <TableCell>{id.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
          {batchType === 'Status' ? (
            <Grid item xs={3}>
              <TextField
                required
                id='statusType'
                name='statusType'
                label='Status'
                select
                fullWidth
                value={statusType}
                onChange={updateStatusType}
              >
                {BookingConstants.STATUSES.map(status => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ) : (
            ''
          )}
          <Grid item xs={3}>
            {batchType === 'Status' ? (
              <Button
                id='add-notes-button'
                variant='contained'
                color='primary'
                disabled={processing}
                onClick={updateStatuses}
              >
                Update Statuses
              </Button>
            ) : (
              ''
            )}
          </Grid>
          <br /> <br />
          <Grid item xs={3}>
            <Button
              id='clear-ids-button'
              variant='contained'
              color='secondary'
              onClick={clearIds}
            >
              Change IDs
            </Button>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

export default withRouter(BookingActions);
